/**
 * V2 API Service
 *
 * All API calls for v2 endpoints (/api/v2/*).
 * Reuses the shared axios instance and Firebase auth from the v1 api module.
 *
 * Based on:
 *  - OneBackend/docs/API_DOCUMENTATION_v2.md
 *  - Actual handler implementations in OneBackend/internal/interfaces/http/handler/v2/
 *  - DTO definitions in OneBackend/internal/interfaces/dto/v2/
 */

import axios, { AxiosError, AxiosInstance } from 'axios';
import auth from '@react-native-firebase/auth';
import { API_BASE_URL } from '../../config/env';
import {V2ApiError, V2NoResponseError} from './errors';
import type {
  V2LoginResponse,
  V2User,
  V2ChangePasswordResponse,
  V2TokenListResponse,
  V2XStock,
  V2XStockListResponse,
  V2NetworkListResponse,
  V2AccountListResponse,
  V2Account,
  V2BalanceListResponse,
  V2Transfer,
  V2TransferListResponse,
  V2HistoryRecordListResponse,
  V2RatesResponse,
  V2RateHistoryPeriod,
  V2RatesHistoryResponse,
  V2CreateTransferRequest,
  V2CreateExchangeRequest,
  V2CreateAccountRequest,
  V2ErrorResponse,
  V2CardDetailResponse,
  V2CardWalletResponse,
  V2CardTransactionsResponse,
  V2CardSensitiveResponse,
  V2WalletInfoResponse,
  V2DocumentListResponse,
  V2CardBlockResponse,
  V2DocumentDownloadResponse,
  V2ChatMessage,
  V2ChatMessagesListResponse,
  V2SupportClientFilter,
  V2SupportClientListResponse,
  V2ChatMessageHistoryResponse,
  V2FCMRegisterResponse,
  V2UnreadCountsResponse,
  V2AppBannerResponse,
  V2PushNotificationListResponse,
  V2TransactionType,
  V2TransactionTypeListResponse,
  V2TransactionStatusListResponse,
  V2HistoryFilters,
  V2CardDeposit,
  V2CreateCardDepositRequest,
  V2CardDepositListResponse,
  V2Deposit,
  V2DepositAccount,
  V2CreateDepositRequest,
  V2DepositListResponse,
  V2DepositProductListResponse,
  V2DepositTopupOptions,
  V2CreateDepositTopupRequest,
  V2DepositTopupResponse,
  V2AccountTopupOptions,
  V2CreateAccountTopupRequest,
  V2AccountTopupResponse,
  V2AccountActionOptions,
  V2CreateAccountCloseRequest,
  V2AccountCloseResponse,
  V2CreateAccountOutflowRequest,
  V2AccountOutflowResponse,
  V2Request,
  V2RequestListResponse,
} from '../../types/v2';

// ============================================================================
// Shared Axios Instance (dedicated to v2)
// ============================================================================

const v2Client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const V2_PREFIX = '/api/v2';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get Firebase ID token for authenticated requests.
 * Throws if user is not authenticated with Firebase.
 */
const getFirebaseIdToken = async (): Promise<string> => {
  const user = auth().currentUser;
  if (!user) {
    throw new Error('Not authenticated with Firebase');
  }
  return user.getIdToken();
};

/**
 * Build Authorization header with Firebase ID token.
 */
const getAuthHeaders = async (): Promise<{ Authorization: string }> => {
  const idToken = await getFirebaseIdToken();
  return { Authorization: `Bearer ${idToken}` };
};

/** Header carrying the key of one attempt of a money request (see useIdempotencyKey). */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';

/**
 * Auth headers, plus the Idempotency-Key when the caller has one: a repeat of
 * the request with the same key gets the first answer from the server instead
 * of filing it again.
 */
const getMoneyHeaders = async (idempotencyKey?: string): Promise<Record<string, string>> => {
  const headers: Record<string, string> = await getAuthHeaders();
  if (idempotencyKey) {
    headers[IDEMPOTENCY_KEY_HEADER] = idempotencyKey;
  }
  return headers;
};

/**
 * Unified error handler. `isProtectedRoute` indicates whether the endpoint
 * requires auth — used to detect session expiration vs. wrong credentials.
 */
const handleV2Error = (error: AxiosError<V2ErrorResponse>, isProtectedRoute = false): never => {
  console.log('[V2 API] Error:', {
    status: error.response?.status,
    data: error.response?.data,
    message: error.message,
    url: error.config?.url,
  });

  if (error.response) {
    const apiError = error.response.data;
    const status = error.response.status;

    if (status === 401 && isProtectedRoute) {
      // "invalid_credentials" on a protected route means the submitted password
      // is wrong (e.g. change-password), NOT that the session expired.
      if (apiError?.error === 'invalid_credentials') {
        throw new V2ApiError(
          apiError?.message || 'Invalid credentials',
          'invalid_credentials',
          status,
        );
      }
      throw new V2ApiError(
        'Session expired. Please login again.',
        apiError?.error || 'unauthorized',
        status,
      );
    }

    throw new V2ApiError(
      apiError?.message || `Server error: ${status}`,
      apiError?.error || 'server_error',
      status,
    );
  } else if (error.request) {
    // A money request whose answer never came may still have been filed: the
    // server can finish after the app stops waiting. Say so, instead of
    // inviting a plain retry.
    if (error.config?.headers?.[IDEMPOTENCY_KEY_HEADER]) {
      throw new V2NoResponseError(
        'No answer from the server in time. Your request may have been sent — check History before trying again.',
      );
    }
    throw new V2NoResponseError('Network error. Please check your connection.');
  } else {
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// ============================================================================
// Public Endpoints (no auth required)
// ============================================================================

export const v2GetMyRequests = async (): Promise<V2RequestListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2RequestListResponse>(
      `${V2_PREFIX}/user/requests`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2GetMyRequest = async (id: string): Promise<V2Request> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2Request>(
      `${V2_PREFIX}/user/requests/${id}`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/tokens — list all active tokens with network info.
 */
export const v2GetTokens = async (): Promise<V2TokenListResponse> => {
  try {
    const response = await v2Client.get<V2TokenListResponse>(`${V2_PREFIX}/tokens`);
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * GET /api/v2/networks — list all active blockchain networks.
 */
export const v2GetNetworks = async (): Promise<V2NetworkListResponse> => {
  try {
    const response = await v2Client.get<V2NetworkListResponse>(`${V2_PREFIX}/networks`);
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * GET /api/v2/rates — exchange rates (optional symbol filter).
 * @param symbols  Optional array of token symbols, e.g. ['BTC', 'ETH']
 */
export const v2GetRates = async (symbols?: string[]): Promise<V2RatesResponse> => {
  try {
    const params = symbols && symbols.length > 0
      ? { symbols: symbols.join(',') }
      : undefined;
    const response = await v2Client.get<V2RatesResponse>(`${V2_PREFIX}/rates`, { params });
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/** GET /api/v2/rates/history — chart-ready historical token prices. */
export const v2GetRatesHistory = async (
  symbols: string[],
  period: V2RateHistoryPeriod,
): Promise<V2RatesHistoryResponse> => {
  try {
    const response = await v2Client.get<V2RatesHistoryResponse>(`${V2_PREFIX}/rates/history`, {
      params: { symbols: symbols.join(','), period },
    });
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * GET /api/v2/xstocks — active xStocks in display order. Public, like /tokens.
 */
export const v2GetXStocks = async (): Promise<V2XStockListResponse> => {
  try {
    const response = await v2Client.get<V2XStockListResponse>(`${V2_PREFIX}/xstocks`);
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * GET /api/v2/xstocks/:symbol — one active xStock (symbol is case-insensitive).
 */
export const v2GetXStock = async (symbol: string): Promise<V2XStock> => {
  try {
    const response = await v2Client.get<V2XStock>(`${V2_PREFIX}/xstocks/${encodeURIComponent(symbol)}`);
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * GET /api/v2/app/banner — get app banner for the current platform & version.
 * @param platform  'ios' or 'android'
 * @param version   Current app version string, e.g. '1.2.0'
 */
export const v2GetAppBanner = async (
  platform: 'ios' | 'android',
  version: string,
): Promise<V2AppBannerResponse> => {
  try {
    const response = await v2Client.get<V2AppBannerResponse>(`${V2_PREFIX}/app/banner`, {
      params: { platform, version },
    });
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * GET /api/v2/app/settings — generic key→JSON feature-flag hash.
 * Returns every flag from app_settings as a JSON map. Missing keys mean
 * "use built-in default" — never throw on absent flags.
 *
 * Current known keys:
 *   show_card_last4 (bool) — render "**** 3476" subtitle on dashboard cards.
 *   xstocks_enabled (bool) — show the xStocks (tokenized stocks) section.
 */
export interface V2AppSettingsResponse {
  settings: Record<string, unknown>;
}

export const v2GetAppSettings = async (): Promise<V2AppSettingsResponse> => {
  try {
    const response = await v2Client.get<V2AppSettingsResponse>(`${V2_PREFIX}/app/settings`);
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

// ============================================================================
// Auth Endpoints
// ============================================================================

/**
 * POST /api/v2/auth/login — authenticate with login & password.
 * Rate-limited: 15 requests/min per IP.
 */
export const v2Login = async (login: string, password: string): Promise<V2LoginResponse> => {
  try {
    const response = await v2Client.post<V2LoginResponse>(`${V2_PREFIX}/auth/login`, {
      login,
      password,
    });
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

export interface V2LoginLockoutRequest {
  login: string;
  device_type: string;
  device_model: string;
  device_os_version: string;
  app_version: string;
}

export interface V2LoginLockoutResponse {
  notified: boolean;
}

/** POST /api/v2/auth/login-lockout — notify the manager after app lockout. */
export const v2NotifyLoginLockout = async (
  payload: V2LoginLockoutRequest,
): Promise<V2LoginLockoutResponse> => {
  try {
    const response = await v2Client.post<V2LoginLockoutResponse>(
      `${V2_PREFIX}/auth/login-lockout`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * GET /api/v2/auth/me — get current user profile.
 */
export const v2GetMe = async (): Promise<V2User> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2User>(`${V2_PREFIX}/auth/me`, { headers });
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Impersonation (super-user only)
// ============================================================================

export interface V2ImpersonateResponse {
  firebase_token: string;
  expires_in: number;
  session_id: string;
  impersonator_login: string;
  target: V2User;
}

export interface V2StopImpersonationResponse {
  firebase_token: string;
  expires_in: number;
  user: V2User;
}

/**
 * POST /api/v2/auth/impersonate — super-user starts a session as target_login.
 * Requires caller's own password (step-up re-auth).
 * Returns a Firebase custom token for the target user with imp claims.
 * The app must sign in with signInWithCustomToken and swap the active token.
 */
export const v2Impersonate = async (
  targetLogin: string,
  callerPassword: string,
  reason?: string,
): Promise<V2ImpersonateResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.post<V2ImpersonateResponse>(
      `${V2_PREFIX}/auth/impersonate`,
      { target_login: targetLogin, caller_password: callerPassword, reason },
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * POST /api/v2/auth/stop-impersonation — ends imp session, returns super-user's token.
 * Must be called from within the impersonated session (the auth middleware reads
 * impersonator_id from the current token's claims).
 */
export const v2StopImpersonation = async (
  targetUserID?: string,
): Promise<V2StopImpersonationResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.post<V2StopImpersonationResponse>(
      `${V2_PREFIX}/auth/stop-impersonation`,
      targetUserID ? { target_user_id: targetUserID } : {},
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>);
  }
};

/**
 * POST /api/v2/user/change-password — change password for current user.
 */
export const v2ChangePassword = async (
  oldPassword: string,
  newPassword: string,
): Promise<V2ChangePasswordResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.post<V2ChangePasswordResponse>(
      `${V2_PREFIX}/user/change-password`,
      { old_password: oldPassword, new_password: newPassword },
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Account Endpoints
// ============================================================================

/**
 * GET /api/v2/user/accounts — list current user's accounts.
 */
export const v2GetMyAccounts = async (): Promise<V2AccountListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2AccountListResponse>(
      `${V2_PREFIX}/user/accounts`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/accounts/balances — balances across all user accounts
 * with enriched token, wallet and network info.
 */
export const v2GetMyAccountBalances = async (): Promise<V2BalanceListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2BalanceListResponse>(
      `${V2_PREFIX}/user/accounts/balances`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/accounts — create a new account for current user.
 */
export const v2CreateAccount = async (
  request: V2CreateAccountRequest,
): Promise<V2Account> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.post<V2Account>(
      `${V2_PREFIX}/user/accounts`,
      request,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Balance Endpoints
// ============================================================================

/**
 * GET /api/v2/user/balances — aggregated balances with USD enrichment.
 * Returns balances + total_usd computed from CoinMarketCap rates.
 */
export const v2GetMyBalances = async (): Promise<V2BalanceListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2BalanceListResponse>(
      `${V2_PREFIX}/user/balances`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Transfer Endpoints
// ============================================================================

/**
 * POST /api/v2/user/transfers — create withdrawal request.
 * Status starts as "pending".
 */
export const v2CreateTransfer = async (
  request: V2CreateTransferRequest,
  idempotencyKey?: string,
): Promise<V2Transfer> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2Transfer>(
      `${V2_PREFIX}/user/transfers`,
      request,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/transfers — list current user's transfers.
 */
export const v2GetMyTransfers = async (): Promise<V2TransferListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2TransferListResponse>(
      `${V2_PREFIX}/user/transfers`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/transfers/:id — get a specific transfer by ID.
 * Returns 403 if the transfer doesn't belong to the current user.
 */
export const v2GetTransfer = async (id: string): Promise<V2Transfer> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2Transfer>(
      `${V2_PREFIX}/user/transfers/${id}`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// History Endpoints
// ============================================================================

/**
 * GET /api/v2/user/history — history records for current user (sorted by sort_order).
 * Supports optional filters: token_symbol, status, date_from, date_to.
 */
export const v2GetMyHistory = async (
  filters?: V2HistoryFilters,
): Promise<V2HistoryRecordListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const params: Record<string, string> = {};
    if (filters?.token_symbol) params.token_symbol = filters.token_symbol;
    if (filters?.status) params.status = filters.status;
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    if (filters?.balance_id) params.balance_id = filters.balance_id;
    const response = await v2Client.get<V2HistoryRecordListResponse>(
      `${V2_PREFIX}/user/history`,
      { headers, params },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * Build an absolute URL to a transaction type's icon. The endpoint is public
 * (no auth) and supports ETag/Cache-Control headers, so React Native's image
 * cache deduplicates fetches across renders. Returns null when the type has
 * no icon set so callers can render a placeholder.
 *
 * The `v=...` query param changes when icon_updated_at changes, so a freshly
 * uploaded icon invalidates the cached entry on next mount without a hard
 * cache flush.
 */
export const v2TransactionTypeIconUrl = (
  type: Pick<V2TransactionType, 'id' | 'has_icon' | 'icon_updated_at'>,
): string | null => {
  if (!type.has_icon) return null;
  const ts = type.icon_updated_at ? new Date(type.icon_updated_at).getTime() : 0;
  return `${API_BASE_URL}${V2_PREFIX}/transaction-types/${type.id}/icon?v=${ts}`;
};

/**
 * GET /api/v2/user/transaction-types — list of transaction types for filter buttons.
 */
export const v2GetTransactionTypes =
  async (): Promise<V2TransactionTypeListResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await v2Client.get<V2TransactionTypeListResponse>(
        `${V2_PREFIX}/user/transaction-types`,
        {headers},
      );
      return response.data;
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('Not authenticated')
      ) {
        throw error;
      }
      throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
    }
  };

/**
 * GET /api/v2/user/transaction-statuses — list of transaction statuses for filter dropdown.
 */
export const v2GetTransactionStatuses =
  async (): Promise<V2TransactionStatusListResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await v2Client.get<V2TransactionStatusListResponse>(
        `${V2_PREFIX}/user/transaction-statuses`,
        {headers},
      );
      return response.data;
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('Not authenticated')
      ) {
        throw error;
      }
      throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
    }
  };

// ============================================================================
// Card (Payoma) Endpoints
// ============================================================================

/**
 * GET /api/v2/user/cards/:id — proxy card details from Payoma
 */
export const v2GetCard = async (id: string): Promise<V2CardDetailResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2CardDetailResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/cards/:id/wallet — card wallet/balance
 */
export const v2GetCardWallet = async (id: string): Promise<V2CardWalletResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2CardWalletResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/wallet`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/cards/:id/transactions?from=...&to=...
 * `from` and `to` are expected by backend in format: "2006-01-02 15:04:05"
 */
export const v2GetCardTransactions = async (
  id: string,
  from?: string,
  to?: string,
): Promise<V2CardTransactionsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await v2Client.get<V2CardTransactionsResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/transactions`,
      { headers, params },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/cards/:id/wallet-info?currency=...
 */
export const v2GetWalletInfo = async (id: string, currency: string): Promise<V2WalletInfoResponse> => {
  try {
    const headers = await getAuthHeaders();
    const params = { currency };
    const response = await v2Client.get<V2WalletInfoResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/wallet-info`,
      { headers, params },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// Sensitive card endpoints (require additional client-side verification)
export const v2GetCardNumber = async (id: string): Promise<V2CardSensitiveResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2CardSensitiveResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/number`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2GetCardPIN = async (id: string): Promise<V2CardSensitiveResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2CardSensitiveResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/pin`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2GetCardCVV = async (id: string): Promise<V2CardSensitiveResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2CardSensitiveResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/cvv`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2GetCardOtpCode = async (id: string): Promise<V2CardSensitiveResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2CardSensitiveResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/otp-code`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * PUT /api/v2/user/cards/:id/block — block a card via Payoma
 */
export const v2BlockCard = async (id: string): Promise<V2CardBlockResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.put<V2CardBlockResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/block`,
      null,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * DELETE /api/v2/user/cards/:id/block — unblock a card via Payoma
 */
export const v2UnblockCard = async (id: string): Promise<V2CardBlockResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.delete<V2CardBlockResponse>(
      `${V2_PREFIX}/user/cards/${encodeURIComponent(id)}/block`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Card Deposit Endpoints
// ============================================================================

/**
 * POST /api/v2/user/card-deposits — create a card deposit request.
 *
 * NOTE: the `fee` field in the request is advisory only. The backend
 * overrides it with a hardcoded 2% commission (see CreateCardDeposit
 * in card_deposit_handler.go). Send the client-side preview fee for
 * audit-friendliness — the stored value always comes from the server.
 */
export const v2CreateCardDeposit = async (
  request: V2CreateCardDepositRequest,
  idempotencyKey?: string,
): Promise<V2CardDeposit> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2CardDeposit>(
      `${V2_PREFIX}/user/card-deposits`,
      request,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/card-deposits — list current user's card deposits.
 */
export const v2GetMyCardDeposits = async (): Promise<V2CardDepositListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2CardDepositListResponse>(
      `${V2_PREFIX}/user/card-deposits`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/card-deposits/fee-rate — returns the current card-deposit
 * commission rate (admin-managed via v1_2_fee_settings, code "card_deposit").
 * Used by DepositCardScreen to render "Fee X% (included)" with the live rate
 * instead of a hardcoded constant. Backend returns its own fallback (0.02)
 * if the row is missing/inactive, so this endpoint never 5xx's on config.
 */
export const v2GetCardDepositFeeRate = async (): Promise<{ code: string; percent: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ code: string; percent: string }>(
      `${V2_PREFIX}/user/card-deposits/fee-rate`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/card-deposits/rate-fee — returns the card-deposit rate-haircut
 * fraction (admin-managed via v1_2_fee_settings, code "card_deposit_rate"). Unlike
 * card_deposit (flat commission on amount), this one is applied to the USDT-EUR
 * conversion rate as convRate * (1 - percent). Backend returns "0" when the row
 * is missing/inactive so the client naturally no-ops the haircut.
 */
export const v2GetCardDepositRateFee = async (): Promise<{ code: string; percent: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ code: string; percent: string }>(
      `${V2_PREFIX}/user/card-deposits/rate-fee`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/transfers/fee-rate — returns the current withdraw fee rate
 * (admin-managed via v1_2_fee_settings, code "withdraw"). Used by
 * WithdrawScreen to render "Fee, X%" with the live rate. Backend falls back
 * to 0.02 if the row is missing/inactive.
 */
export const v2GetWithdrawFeeRate = async (): Promise<{ code: string; percent: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ code: string; percent: string }>(
      `${V2_PREFIX}/user/transfers/fee-rate`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/transfers/min-amount — returns the live minimum-withdraw
 * threshold (admin-managed via v1_2_amount_settings, code "min_amount_withdraw").
 * WithdrawScreen shows it as "Minimum amount" and blocks Confirm when the
 * entered amount is below the threshold.
 */
export const v2GetMinWithdrawAmount = async (): Promise<{ code: string; value: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ code: string; value: string }>(
      `${V2_PREFIX}/user/transfers/min-amount`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Exchange Endpoints (WAL-14)
// ============================================================================

/**
 * POST /api/v2/user/exchange — create an exchange (limit) order.
 *
 * Returns the created order (status starts as "pending"). The backend locks
 * amount + fee on the sell balance and writes a pending history record; the
 * order is not matched or executed by this call.
 */
export const v2CreateExchange = async (
  request: V2CreateExchangeRequest,
  idempotencyKey?: string,
): Promise<V2Request> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2Request>(
      `${V2_PREFIX}/user/exchange`,
      request,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/exchange/fee-rate — current exchange fee rate
 * (admin-managed via v1_2_fee_settings, code "exchange"). Rendered as
 * "Fee, X%" on the Exchange screen; the fee itself is charged in the sell
 * token. Backend falls back to 0.005 when the row is missing or inactive.
 */
/** Fee rate for one order type; without a type the server answers with the limit one. */
export const v2GetExchangeFeeRate = async (
  orderType?: 'market' | 'limit',
): Promise<{ code: string; percent: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ code: string; percent: string }>(
      `${V2_PREFIX}/user/exchange/fee-rate${orderType ? `?type=${orderType}` : ''}`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/exchange/min-amount — live minimum sell amount
 * (admin-managed via v1_2_amount_settings, code "min_amount_exchange").
 * Shown as "Minimum amount"; Confirm stays disabled below it.
 */
export const v2GetMinExchangeAmount = async (
  tokenId?: string,
): Promise<{ code: string; value: string; currency?: string }> => {
  try {
    const headers = await getAuthHeaders();
    // With a token: that token's own minimum (in its units) when the Amounts
    // reference has one, else the general minimum in USDT.
    const response = await v2Client.get<{ code: string; value: string; currency?: string }>(
      `${V2_PREFIX}/user/exchange/min-amount`,
      { headers, params: tokenId ? { token_id: tokenId } : undefined },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/card-deposits/min-amount — returns the live minimum
 * top-up threshold (admin-managed via v1_2_amount_settings, code
 * "min_amount_top_up"). DepositCardScreen shows it as "Minimum amount" and
 * blocks Confirm when the entered amount is below the threshold.
 */
export const v2GetMinTopUpAmount = async (): Promise<{ code: string; value: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ code: string; value: string }>(
      `${V2_PREFIX}/user/card-deposits/min-amount`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Term Deposit Endpoints
// ============================================================================

/**
 * POST /api/v2/user/deposits — open a term deposit. Locks `amount` on the
 * source balance until status moves to confirmed / cancelled.
 */
export const v2CreateDeposit = async (
  request: V2CreateDepositRequest,
  idempotencyKey?: string,
): Promise<V2Deposit> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2Deposit>(
      `${V2_PREFIX}/user/deposits`,
      request,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/deposits — list current user's deposits, most recent first.
 */
export const v2GetMyDeposits = async (): Promise<V2DepositListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2DepositListResponse>(
      `${V2_PREFIX}/user/deposits`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/accounts/:id/deposit-view — canonical aggregate read
 * for the OneApp "Deposit account" screen, keyed on the investment
 * account itself (no v1_2_deposits row required). Works for both new
 * and legacy investment accounts; legacy fields come back empty/zero
 * so the UI can render `—`.
 */
export const v2GetAccountDepositView = async (
  accountId: string,
): Promise<V2DepositAccount> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2DepositAccount>(
      `${V2_PREFIX}/user/accounts/${accountId}/deposit-view`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/accounts/:id/topup-options — account-based variant
 * of /deposits/:id/topup-options. Drives the Top-up screen when opened
 * against an investment account directly.
 */
export const v2GetAccountTopupOptions = async (
  accountId: string,
): Promise<V2AccountTopupOptions> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2AccountTopupOptions>(
      `${V2_PREFIX}/user/accounts/${accountId}/topup-options`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/accounts/:id/topup — Inflow against an investment
 * account directly. Mirror of v2CreateDepositTopup but doesn't require
 * a v1_2_deposits row.
 */
export const v2CreateAccountTopup = async (
  accountId: string,
  payload: V2CreateAccountTopupRequest,
  idempotencyKey?: string,
): Promise<V2AccountTopupResponse> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2AccountTopupResponse>(
      `${V2_PREFIX}/user/accounts/${accountId}/topup`,
      payload,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/accounts/:id/close-options — drives OneApp's Close
 * Deposit screen. Single round-trip with the source investment balance,
 * full account meta (term/apy/dates), and destination candidates
 * (current-account balances matching the deposit's symbol).
 */
export const v2GetAccountCloseOptions = async (
  accountId: string,
): Promise<V2AccountActionOptions> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2AccountActionOptions>(
      `${V2_PREFIX}/user/accounts/${accountId}/close-options`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/accounts/:id/close — request closing the deposit.
 * Backend freezes the full investment balance amount and creates the
 * close pair (close_deposit_account + transfer_deposit_to_current).
 * Admin confirm settles via AdminUpdateDeposit dispatcher.
 */
export const v2CreateAccountClose = async (
  accountId: string,
  payload: V2CreateAccountCloseRequest,
  idempotencyKey?: string,
): Promise<V2AccountCloseResponse> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2AccountCloseResponse>(
      `${V2_PREFIX}/user/accounts/${accountId}/close`,
      payload,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/accounts/:id/outflow-options — drives the OneApp
 * "Withdraw deposit" screen. Same shape as close-options plus
 * `available_to_withdraw` (20 % of initial open amount before
 * maturity) and `is_before_maturity` (drives the amber warning bar).
 */
export const v2GetAccountOutflowOptions = async (
  accountId: string,
): Promise<V2AccountActionOptions> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2AccountActionOptions>(
      `${V2_PREFIX}/user/accounts/${accountId}/outflow-options`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/accounts/:id/outflow — request a partial
 * withdrawal. Backend locks the amount on the investment balance and
 * creates the outflow pair (outflow_deposit_account +
 * transfer_deposit_to_current). On admin confirm AdminUpdateDeposit
 * dispatcher applies the fee (subtracted from the credit amount).
 */
export const v2CreateAccountOutflow = async (
  accountId: string,
  payload: V2CreateAccountOutflowRequest,
  idempotencyKey?: string,
): Promise<V2AccountOutflowResponse> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2AccountOutflowResponse>(
      `${V2_PREFIX}/user/accounts/${accountId}/outflow`,
      payload,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/deposits/min-amount — minimum deposit threshold
 * (admin-managed via v1_2_amount_settings, code "min_amount_deposit").
 */
export const v2GetMinDepositAmount = async (): Promise<{ code: string; value: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ code: string; value: string }>(
      `${V2_PREFIX}/user/deposits/min-amount`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/deposit-products — active catalog rows for the
 * "Open deposit" screen. Filter by `currency` to narrow to the rows
 * relevant to a single source balance. The backend already restricts
 * the result to `is_active && !is_deleted` rows on this route.
 */
export const v2GetDepositProducts = async (
  currency?: string,
): Promise<V2DepositProductListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2DepositProductListResponse>(
      `${V2_PREFIX}/user/deposit-products`,
      { headers, params: currency ? { currency } : undefined },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Document Endpoints
// ============================================================================

/**
 * GET /api/v2/user/documents — list current user's documents.
 */
export const v2GetMyDocuments = async (): Promise<V2DocumentListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2DocumentListResponse>(
      `${V2_PREFIX}/user/documents`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/documents/:id/download — get presigned download URL.
 */
export const v2GetDocumentDownloadUrl = async (
  documentId: string,
): Promise<V2DocumentDownloadResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2DocumentDownloadResponse>(
      `${V2_PREFIX}/user/documents/${encodeURIComponent(documentId)}/download`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/documents/mark-viewed — mark all documents as viewed.
 */
export const v2MarkDocumentsViewed = async (): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await v2Client.post(
      `${V2_PREFIX}/user/documents/mark-viewed`,
      {},
      { headers },
    );
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Chat Endpoints
// ============================================================================

/**
 * GET /api/v2/user/chat/messages — list all chat messages for current user.
 */
export const v2GetChatMessages = async (): Promise<V2ChatMessagesListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2ChatMessagesListResponse>(
      `${V2_PREFIX}/user/chat/messages`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * GET /api/v2/user/chat/messages/unread — list unread chat messages.
 */
export const v2GetUnreadChatMessages = async (): Promise<V2ChatMessagesListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2ChatMessagesListResponse>(
      `${V2_PREFIX}/user/chat/messages/unread`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/chat/messages — send text message.
 */
export const v2SendChatMessage = async (content: string): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.post<V2ChatMessage>(
      `${V2_PREFIX}/user/chat/messages`,
      { content },
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * PATCH /api/v2/user/chat/messages/:messageId — edit own user-authored chat
 * message. Backend проверяет, что сообщение принадлежит юзеру И что это
 * user-sender (не admin-message). Audit-таблица v1_2_chat_messages_hist
 * автоматически фиксирует каждую правку через DB-триггер.
 */
export const v2EditChatMessage = async (
  messageId: string,
  content: string,
): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.patch<V2ChatMessage>(
      `${V2_PREFIX}/user/chat/messages/${messageId}`,
      { content },
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * DELETE /api/v2/user/chat/messages/:messageId — soft-delete own user-authored
 * message. Backend проверяет ownership и sender_type=user; row остаётся в
 * таблице с deleted_at и content=NULL. Audit-таблица фиксирует OLD-image.
 * Response — обновлённое сообщение (tombstone).
 */
export const v2DeleteChatMessage = async (
  messageId: string,
): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.delete<V2ChatMessage>(
      `${V2_PREFIX}/user/chat/messages/${messageId}`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Card Applications — "Open card" заявки. POST создаётся при нажатии
// "Send request" на Card-PDF в Products. Параллельно сохраняется
// существующий вызов v2SendChatMessage('Get the card') — заявка и чат-
// сообщение работают вместе, заявка не замещает сообщение.
// ============================================================================

/** Discriminator для 409-ответов CreateCardApplication. Используется
 *  LocalPDFPreviewScreen для дружелюбных toast'ов. */
export type CardApplicationConflictKind = 'active_application_exists' | 'card_already_issued';

/** Кастомный error class — несёт тип конфликта, чтобы UI мог различать
 *  "уже подал заявку" vs "уже есть карта" без парсинга message. Наследник
 *  Error, поэтому остальной error-handling кодом ловится как обычная
 *  ошибка, если кто-то не сделал явный instanceof. */
export class CardApplicationConflictError extends Error {
  constructor(public readonly kind: CardApplicationConflictKind, message: string) {
    super(message);
    this.name = 'CardApplicationConflictError';
  }
}

/** Минимальный shape ответа от backend (CardApplicationDTO). Для текущего
 *  flow в OneApp нужен только id — расширим, когда появится экран статусов. */
export interface V2CardApplication {
  id: string;
  user_id: string;
  transaction_status_id: string;
  status_name?: string;
  admin_comment?: string | null;
  resolved_at?: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * POST /api/v2/user/card-applications — создаёт заявку на выпуск карты.
 *
 * Идемпотентность гарантирует backend:
 *   - 409 `active_application_exists` если у юзера уже есть pending/in_process
 *     заявка → бросаем CardApplicationConflictError с этим kind.
 *   - 409 `card_already_issued` если у юзера уже есть card_balance → то же,
 *     но с другим kind.
 * Любая другая ошибка идёт через общий handleV2Error.
 */
export const v2CreateCardApplication = async (idempotencyKey?: string): Promise<V2CardApplication> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2CardApplication>(
      `${V2_PREFIX}/user/card-applications`,
      {},
      { headers },
    );
    return response.data;
  } catch (error) {
    const ax = error as AxiosError<V2ErrorResponse & { application_id?: string }>;
    if (ax.response?.status === 409) {
      const errCode = ax.response.data?.error as CardApplicationConflictKind | undefined;
      const msg = ax.response.data?.message || 'Card application conflict';
      if (errCode === 'active_application_exists' || errCode === 'card_already_issued') {
        throw new CardApplicationConflictError(errCode, msg);
      }
    }
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(ax, true);
  }
};

export type V2InvestmentProduct = 't_fund' | 'personal_investment';

/** 409 from investment-requests: a request of that product is already in
 *  progress (pending / in_process). */
export class InvestmentRequestConflictError extends Error {
  constructor(public readonly requestId: string | undefined, message: string) {
    super(message);
    this.name = 'InvestmentRequestConflictError';
  }
}

/**
 * POST /api/v2/user/investment-requests — T-FUND / Personal investment
 * request from the Products screen. No amount: the request goes to OneAdmin
 * Orders as pending. A second one while the first is in progress throws
 * InvestmentRequestConflictError.
 */
export const v2CreateInvestmentRequest = async (
  product: V2InvestmentProduct,
  idempotencyKey?: string,
): Promise<V2Request> => {
  try {
    const headers = await getMoneyHeaders(idempotencyKey);
    const response = await v2Client.post<V2Request>(
      `${V2_PREFIX}/user/investment-requests`,
      { product },
      { headers },
    );
    return response.data;
  } catch (error) {
    const ax = error as AxiosError<V2ErrorResponse & { request_id?: string }>;
    if (ax.response?.status === 409 && ax.response.data?.error === 'active_request_exists') {
      throw new InvestmentRequestConflictError(
        ax.response.data.request_id,
        ax.response.data.message || 'There is already a request in progress',
      );
    }
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(ax, true);
  }
};

/**
 * POST /api/v2/user/chat/messages — send message with file attachment (multipart).
 */
export const v2SendChatMessageWithAttachment = async (
  content: string | null,
  file: { uri: string; name: string; type: string },
): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const formData = new FormData();
    if (content) {
      formData.append('content', content);
    }
    formData.append('file', {
      uri: file.uri,
      name: file.name,
      type: file.type,
    } as any);

    const response = await v2Client.post<V2ChatMessage>(
      `${V2_PREFIX}/user/chat/messages`,
      formData,
      {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/chat/read — mark all chat messages as read.
 */
export const v2MarkChatRead = async (): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await v2Client.post(
      `${V2_PREFIX}/user/chat/read`,
      {},
      { headers },
    );
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Super-user client support endpoints
// ============================================================================

const supportClientPath = (userId: string): string =>
  `${V2_PREFIX}/user/admin/support/clients/${encodeURIComponent(userId)}`;

export const v2ListSupportClients = async (params: {
  search?: string;
  filter?: V2SupportClientFilter;
  limit?: number;
  offset?: number;
} = {}): Promise<V2SupportClientListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2SupportClientListResponse>(
      `${V2_PREFIX}/user/admin/support/clients`,
      { headers, params },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2GetSupportUnreadCount = async (): Promise<number> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<{ unread_count: number }>(
      `${V2_PREFIX}/user/admin/support/unread`,
      { headers },
    );
    return response.data.unread_count || 0;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2GetSupportClientMessages = async (
  userId: string,
): Promise<V2ChatMessagesListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2ChatMessagesListResponse>(
      `${supportClientPath(userId)}/messages`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2SendSupportClientMessage = async (
  userId: string,
  content: string,
): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.post<V2ChatMessage>(
      `${supportClientPath(userId)}/messages`,
      { content },
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2SendSupportClientMessageWithAttachment = async (
  userId: string,
  content: string | null,
  file: { uri: string; name: string; type: string },
): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const formData = new FormData();
    if (content) formData.append('content', content);
    formData.append('file', file as any);
    const response = await v2Client.post<V2ChatMessage>(
      `${supportClientPath(userId)}/messages`,
      formData,
      { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2EditSupportClientMessage = async (
  userId: string,
  messageId: string,
  content: string,
): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.patch<V2ChatMessage>(
      `${supportClientPath(userId)}/messages/${encodeURIComponent(messageId)}`,
      { content },
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2DeleteSupportClientMessage = async (
  userId: string,
  messageId: string,
): Promise<V2ChatMessage> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.delete<V2ChatMessage>(
      `${supportClientPath(userId)}/messages/${encodeURIComponent(messageId)}`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2GetSupportClientMessageHistory = async (
  userId: string,
  messageId: string,
): Promise<V2ChatMessageHistoryResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2ChatMessageHistoryResponse>(
      `${supportClientPath(userId)}/messages/${encodeURIComponent(messageId)}/history`,
      { headers },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

export const v2MarkSupportClientRead = async (userId: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await v2Client.post(`${supportClientPath(userId)}/read`, {}, { headers });
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// FCM Endpoints
// ============================================================================

/**
 * POST /api/v2/user/fcm/register — register FCM token for push notifications.
 */
export const v2RegisterFCMToken = async (
  token: string,
  deviceType: 'ios' | 'android',
): Promise<V2FCMRegisterResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.post<V2FCMRegisterResponse>(
      `${V2_PREFIX}/user/fcm/register`,
      { token, device_type: deviceType },
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * DELETE /api/v2/user/fcm/unregister — unregister a specific FCM token.
 */
export const v2UnregisterFCMToken = async (token: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await v2Client.delete(`${V2_PREFIX}/user/fcm/unregister`, {
      headers,
      data: { token },
    });
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * DELETE /api/v2/user/fcm/unregister-all — unregister all FCM tokens for user.
 */
export const v2UnregisterAllFCMTokens = async (): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await v2Client.delete(`${V2_PREFIX}/user/fcm/unregister-all`, {
      headers,
    });
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Push Notification History Endpoints
// ============================================================================

/**
 * GET /api/v2/user/push/history — get push notification history for current user.
 */
export const v2GetMyPushNotifications = async (): Promise<V2PushNotificationListResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2PushNotificationListResponse>(
      `${V2_PREFIX}/user/push/history`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

/**
 * POST /api/v2/user/push/mark-viewed — mark all push notifications as viewed.
 */
export const v2MarkPushNotificationsViewed = async (): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    await v2Client.post(
      `${V2_PREFIX}/user/push/mark-viewed`,
      {},
      { headers },
    );
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// Unread Counts Endpoints
// ============================================================================

/**
 * GET /api/v2/user/unread-counts — get unread chat messages and unviewed documents counts.
 */
export const v2GetUnreadCounts = async (): Promise<V2UnreadCountsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2UnreadCountsResponse>(
      `${V2_PREFIX}/user/unread-counts`,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Not authenticated')) {
      throw error;
    }
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};

// ============================================================================
// User Sessions (audit log)
// ============================================================================

export interface V2StartSessionRequest {
  device_type?: string;
  device_model?: string;
  device_os_version?: string;
  app_version?: string;
}

export interface V2StartSessionResponse {
  session_id: string;
  started_at: string;
}

/**
 * POST /api/v2/user/sessions/start — open a session row in the audit log
 * right after a successful Firebase login. Returns the session_id which the
 * client persists in AsyncStorage so it can /end later.
 */
export const v2StartSession = async (
  payload: V2StartSessionRequest,
): Promise<V2StartSessionResponse> => {
  const headers = await getAuthHeaders();
  const response = await v2Client.post<V2StartSessionResponse>(
    `${V2_PREFIX}/user/sessions/start`,
    payload,
    { headers },
  );
  return response.data;
};

/**
 * POST /api/v2/user/sessions/:id/end — close an open session row.
 * Idempotent on the backend: calling /end twice returns 200 both times, so a
 * naive client retry policy is safe.
 */
export const v2EndSession = async (
  sessionId: string,
  endReason?: string,
): Promise<void> => {
  const headers = await getAuthHeaders();
  await v2Client.post(
    `${V2_PREFIX}/user/sessions/${sessionId}/end`,
    endReason ? { end_reason: endReason } : {},
    { headers },
  );
};

// ----------------------------------------------------------------------------
// Super-user mirror of /admin/sessions for OneApp (used by /screens/Profile
// → UserSessionsScreen). Backend checks v1_2_users.is_super_user; non-super
// callers get 403. See API_DOCUMENTATION_v2 §32.
// ----------------------------------------------------------------------------

export type V2SessionSortKey = 'started_at' | 'ended_at' | 'duration_seconds';

export interface V2UserSessionDTO {
  id: string;
  user_id: string;
  user_login?: string;
  started_at: string;
  ended_at?: string | null;
  duration_seconds?: number | null;
  device_type?: string | null;
  device_model?: string | null;
  device_os_version?: string | null;
  app_version?: string | null;
  ip_address?: string | null;
  end_reason?: string | null;
}

export interface V2ListUserSessionsResponse {
  sessions: V2UserSessionDTO[];
  total: number;
  limit: number;
  offset: number;
}

export interface V2ListUserSessionsParams {
  user_id?: string;
  from?: string;
  to?: string;
  sort?: V2SessionSortKey;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * GET /api/v2/user/admin/sessions — super-user-gated session list (same data
 * the admin web at /v2/sessions returns, mirrored to mobile).
 *
 * Throws if the caller doesn't have is_super_user. Use `user?.is_super_user`
 * from AuthContext to hide the entry point before calling.
 */
export const v2ListSuperUserSessions = async (
  params: V2ListUserSessionsParams = {},
): Promise<V2ListUserSessionsResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await v2Client.get<V2ListUserSessionsResponse>(
      `${V2_PREFIX}/user/admin/sessions`,
      { headers, params },
    );
    return response.data;
  } catch (error) {
    throw handleV2Error(error as AxiosError<V2ErrorResponse>, true);
  }
};
